/**
 * « Journal » — the administrative audit trail: who changed what, and when.
 *
 * Distinct from « Activité », which is the gate-scan feed. This page is gated
 * on `canManage` (not `canViewActivity`): a résidence can withhold visitor
 * comings-and-goings from its own syndic, but never the record of the syndic's
 * own administrative actions.
 */
import type { ColumnDef } from "@gately/ui/data-table";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod/v4";

import { DataTable } from "@gately/ui/data-table";
import { DateTimePicker } from "@gately/ui/date-time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { AUDIT_FAMILIES } from "@gately/validators";

import { dateKey } from "~/lib/dashboard-format";
import {
  actionLabel,
  actorRoleLabel,
  auditSourceLabel,
  detailsSummary,
  familyLabel,
} from "~/lib/journal-format";
import { orpc, orpcClient } from "~/lib/orpc";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

const DATE_KEY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** « Toutes les familles » — Radix has no empty-string item value, so the
 *  unfiltered state needs a sentinel that never reaches the server. */
const ALL_FAMILIES = "all";

const searchSchema = z.object({
  // Closed to the shared taxonomy: `audit.list` rejects anything else (the
  // value is spliced into a LIKE pattern server-side), so a stale or
  // hand-edited `?family=` is dropped here rather than 400ing the page.
  family: z
    .enum([...AUDIT_FAMILIES])
    .optional()
    .catch(undefined),
  from: DATE_KEY.optional().catch(undefined),
  to: DATE_KEY.optional().catch(undefined),
});

type JournalSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_dashboard/journal")({
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canManage) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: JournalPage,
});

const PAGE_SIZE = 50;

function filterInput(search: JournalSearch) {
  return {
    ...(search.family ? { family: search.family } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
  };
}

type JournalRow = Awaited<
  ReturnType<typeof orpcClient.audit.list>
>["items"][number];

function JournalPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const terms = useTerms();

  const filters = filterInput(search);

  const query = useInfiniteQuery({
    queryKey: [...orpc.audit.list.key(), filters],
    queryFn: ({ pageParam }) =>
      orpcClient.audit.list({
        ...filters,
        limit: PAGE_SIZE,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  // The org's visibility tier, echoed by the server with every page.
  const windowMonths = query.data?.pages[0]?.windowMonths;

  function setFilter(patch: Partial<JournalSearch>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  const columns: ColumnDef<JournalRow>[] = [
    {
      accessorKey: "createdAt",
      size: 150,
      minSize: 130,
      enableHiding: false,
      meta: { label: "Date" },
      header: "Date",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground whitespace-nowrap">
          {new Date(getValue<string | Date>()).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
    {
      accessorKey: "actorLabel",
      size: 200,
      minSize: 150,
      enableHiding: false,
      meta: { label: "Acteur" },
      header: "Acteur",
      cell: ({ row }) => {
        const role = actorRoleLabel(row.original.actorRole, terms);
        return (
          <div className="leading-tight">
            <div className="text-foreground">{row.original.actorLabel}</div>
            {role ? (
              <div className="text-muted-foreground text-xs">{role}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      accessorKey: "action",
      size: 230,
      minSize: 170,
      meta: { label: "Action" },
      header: "Action",
      cell: ({ getValue }) => actionLabel(getValue<string>(), terms),
    },
    {
      accessorKey: "targetLabel",
      size: 200,
      minSize: 140,
      meta: { label: "Cible" },
      header: "Cible",
      // Some actions have no target at all (a sign-in) — the dash is the row,
      // not a loading state.
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    {
      accessorKey: "details",
      size: 230,
      minSize: 150,
      enableSorting: false,
      meta: { label: "Détails" },
      header: "Détails",
      cell: ({ row }) => {
        const summary = detailsSummary(
          row.original.action,
          row.original.details,
          terms,
        );
        return <span className="text-muted-foreground">{summary || "—"}</span>;
      },
    },
    {
      accessorKey: "source",
      size: 120,
      minSize: 100,
      meta: { label: "Source" },
      header: "Source",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground">
          {auditSourceLabel(getValue<string | null>())}
        </span>
      ),
    },
  ];

  return (
    <div className="px-7 py-6">
      <div className="mb-5">
        <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
          Journal
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Les actions d'administration {terms.ofTheOrg}.
          {/* « visible », never « conservé » : the tier is a read-time
              visibility window, not a deletion guarantee — nothing is erased
              at 12 months (spec: Retention). */}
          {windowMonths ? ` Historique visible : ${windowMonths} mois.` : ""}
        </p>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={query.isPending}
        initialPinning={{ left: ["createdAt"] }}
        showColumnToggle
        toolbar={
          <>
            <Select
              value={search.family ?? ALL_FAMILIES}
              onValueChange={(v) =>
                setFilter({
                  family:
                    v === ALL_FAMILIES
                      ? undefined
                      : (v as NonNullable<JournalSearch["family"]>),
                })
              }
            >
              <SelectTrigger className="h-9 w-48">
                <SelectValue placeholder="Toutes les familles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FAMILIES}>
                  Toutes les familles
                </SelectItem>
                {AUDIT_FAMILIES.map((f) => (
                  <SelectItem key={f} value={f}>
                    {familyLabel(f, terms)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={
                search.from ? new Date(`${search.from}T00:00:00`) : undefined
              }
              onChange={(d) => setFilter({ from: d ? dateKey(d) : undefined })}
              placeholder="Du"
            />
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={search.to ? new Date(`${search.to}T00:00:00`) : undefined}
              onChange={(d) => setFilter({ to: d ? dateKey(d) : undefined })}
              placeholder="Au"
            />
            {search.family || search.from || search.to ? (
              <button
                type="button"
                onClick={() =>
                  setFilter({
                    family: undefined,
                    from: undefined,
                    to: undefined,
                  })
                }
                className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
              >
                Effacer les filtres
              </button>
            ) : null}
          </>
        }
        empty={{
          title: "Aucun événement",
          description: `Le journal des actions ${terms.ofTheOrg} apparaîtra ici.`,
        }}
        manual={{
          hasNextPage: query.hasNextPage,
          isFetchingNextPage: query.isFetchingNextPage,
          onLoadMore: () => void query.fetchNextPage(),
        }}
      />
    </div>
  );
}
